export const AppConstants = Object.freeze({
  BRANDING_PANDORA: 'Pandora',
  BRANDING_PANDORA_PLUS: 'PandoraPlus',
  BRANDING_PANDORA_PREMIUM: 'PandoraPremium',
  BRANDING_BUSINESS: 'Business',
  PANDORA_BLUE: '#224099',
  DEFAULT_GREY: '#666666',
} as const);

export const AppActions = Object.freeze({
  INITIALIZE_CLIENT: 'INITIALIZE_CLIENT',
  APPLICATION_INITIALIZE: 'APPLICATION_INITIALIZE',
  APPLICATION_BACKGROUND_UPDATE: 'APPLICATION_BACKGROUND_UPDATE',
  UPDATE_DOMINANT_COLOR: 'UPDATE_DOMINANT_COLOR',

  SERVICE_OK: 'SERVICE_OK',
  SERVICE_ERROR: 'SERVICE_ERROR',
  TRY_POKING_NETWORK: 'TRY_POKING_NETWORK',

  /* force a transparent re-auth for current listener. */
  LISTENER_REAUTH: 'LISTENER_REAUTH',

  /* service calls currently unavailable. need to hold on to them until back. */
  BUFFER_API_CALLS: 'BUFFER_API_CALLS',

  SET_SCROLL_POS: 'SET_SCROLL_POS',
  SCROLLING_ENABLED: 'SCROLLING_ENABLED',
  BODY_SCROLLING_ENABLED: 'BODY_SCROLLING_ENABLED',
  ENABLE_SCROLLING: 'ENABLE_SCROLLING',
  DISABLE_SCROLLING: 'DISABLE_SCROLLING',
  ENABLE_BODY_SCROLLING: 'ENABLE_BODY_SCROLLING',
  DISABLE_BODY_SCROLLING: 'DISABLE_BODY_SCROLLING',
  ENTER_404: 'ENTER_404',
  LEAVE_404: 'LEAVE_404',
  SHOW_COACHMARK: 'SHOW_COACHMARK',
  SET_PERSISTING_STATE: 'SET_PERSISTING_STATE',
  NOTIFY_CRITICAL_RENDER_PATH: 'NOTIFY_CRITICAL_RENDER_PATH',
  SET_STARTUP_QUERY_PARAMETERS: 'SET_STARTUP_QUERY_PARAMETERS',
  SET_IS_AD_BLOCKER_DETECTED: 'SET_IS_AD_BLOCKER_DETECTED',
  SET_IS_BROWSER_AUTOPLAY_BLOCK_DETECTED: 'SET_IS_BROWSER_AUTOPLAY_BLOCK_DETECTED',
  SET_IS_ANTI_AD_BLOCKER_HAMMER_MODE_ENABLED: 'SET_IS_ANTI_AD_BLOCKER_HAMMER_MODE_ENABLED',
  SET_BROWSER_AUTOPLAY_BLOCK_OPPORTUNITY_HAPPENED:
    'SET_BROWSER_AUTOPLAY_BLOCK_OPPORTUNITY_HAPPENED',
  DETECT_AD_BLOCKER: 'DETECT_AD_BLOCKER',
  OPEN_AD_BLOCKER_MODAL: 'OPEN_AD_BLOCKER_MODAL',
  CLOSE_AD_BLOCKER_MODAL: 'CLOSE_AD_BLOCKER_MODAL',
  OPEN_LINKED_PLAYLIST_UNLOCKED_MODAL: 'OPEN_LINKED_PLAYLIST_UNLOCKED_MODAL',
  PAGE_VISIBILITY_DID_CHANGE: 'PAGE_VISIBILITY_DID_CHANGE',
  ENTER_PLAYLIST: 'ENTER_PLAYLIST',
  LEAVE_PLAYLIST: 'LEAVE_PLAYLIST',
  ENTER_CURATOR_BSP: 'ENTER_CURATOR_BSP',
  LEAVE_CURATOR_BSP: 'LEAVE_CURATOR_BSP',
  ENTER_SEARCH: 'ENTER_SEARCH',
  LEAVE_SEARCH: 'LEAVE_SEARCH',
  UPLOAD_STORE: 'UPLOAD_STORE',
  TOGGLE_OFFLINE_MODE: 'TOGGLE_OFFLINE_MODE',
  SET_LAST_OFFLINE_ACTION: 'SET_LAST_OFFLINE_ACTION',
  REROUTE: 'REROUTE',
  MANUAL_GARBAGE_COLLECTION: 'MANUAL_GARBAGE_COLLECTION',
  EXTERNAL_REDIRECT: 'EXTERNAL_REDIRECT',
} as const);

export const AUTOPLAY_BROWSER_BLOCK_MODAL_COUNT_COOKIE =
  'AUTOPLAY_BROWSER_BLOCK_MODAL_COUNT_COOKIE';
export const AUTOPLAY_BROWSER_BLOCK_MODAL_MAX_COUNT = 2;
export const AUTOPLAY_BROWSER_BLOCK_ANALYTICS_ACTION = Object.freeze({
  PLAYBACK_BLOCKED: 'playback_blocked',
  INITIAL_PLAYBACK_CLICKED: 'initial_playback_clicked',
  MODAL_DISPLAY: 'modal_display',
  SHOW_ME_HOW: 'show_me_how',
  NOT_NOW: 'not_now',
} as const);

export const PREMIUM_PLAYLIST_ANALYTICS_ACTION = Object.freeze({
  MODAL_DISPLAY: 'modal_display',
  SHOW_PLAYLIST: 'show_playlist',
  CLOSE: 'close',
} as const);

export const AppBgKeys = Object.freeze({
  APP_BG: 'appBackground',
  NAV_THEME: 'navTheme',
  BG_THEME: 'bgTheme',
  DOMINANT_COLOR: 'dominantColor',
} as const);

export const NavThemes = Object.freeze({
  TRANSPARENT: 'transparent',
  DARK: 'dark',
  LIGHT: 'light',
  WHITE: 'white',
  DARK_TO_LIGHT: 'darkToLight',
  SEARCH_PAGE: 'searchPage',
} as const);

export const BgThemes = Object.freeze({
  WHITE: 'white',
  IMG: 'img',
  DEFAULT: 'default',
  UPGRADE: 'upgrade',
  NOT_FOUND: 'notFound',
} as const);

export const BgSettings = Object.freeze({
  APP_WHITE: {
    [AppBgKeys.BG_THEME]: BgThemes.WHITE,
    [AppBgKeys.NAV_THEME]: NavThemes.LIGHT,
  },
  APP_IMG: {
    [AppBgKeys.BG_THEME]: BgThemes.IMG,
    [AppBgKeys.NAV_THEME]: NavThemes.TRANSPARENT,
  },
  APP_IMG_SCROLLED: {
    [AppBgKeys.BG_THEME]: BgThemes.IMG,
    [AppBgKeys.NAV_THEME]: NavThemes.DARK,
  },
  APP_DEFAULT: {
    [AppBgKeys.BG_THEME]: BgThemes.DEFAULT,
    [AppBgKeys.NAV_THEME]: NavThemes.DARK,
  },
  APP_UPGRADE: {
    [AppBgKeys.BG_THEME]: BgThemes.UPGRADE,
    [AppBgKeys.NAV_THEME]: NavThemes.WHITE,
  },
  APP_NOT_FOUND: {
    [AppBgKeys.BG_THEME]: BgThemes.NOT_FOUND,
    [AppBgKeys.NAV_THEME]: NavThemes.DARK_TO_LIGHT,
  },
  NAV_DARK_TO_LIGHT: {
    [AppBgKeys.NAV_THEME]: NavThemes.DARK_TO_LIGHT,
  },
} as const);

export const AudioFormats = Object.freeze({
  LOWEST_QUALITY: 'aacplus',
  HIGHEST_QUALITY: 'mp3-hifi',
} as const);

export const AudioFormatToMediaElementTypes = Object.freeze({
  [AudioFormats.LOWEST_QUALITY]: 'audio/m4a',
  [AudioFormats.HIGHEST_QUALITY]: 'audio/mp3',
} as const);

export const KeyCodes = Object.freeze({
  ENTER: 13,
  SHIFT: 16,
  SPACE: 32,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39,
  ESCAPE_KEY: 27,
  UP_ARROW_KEY: 38,
  DOWN_ARROW_KEY: 40,
  BACK_SPACE_KEY: 8,
  TAB: 9,
  LEFT_COMMAND: 91,
  RIGHT_COMMAND: 93,
  FIREFOX_COMMAND: 224,
} as const);

export const PERSISTOR_KEY = 'CAf103jPGIeWM2o7GXxTZX9bZo5WGTfkvDqta5FBkLnQ1TT9tjAOOLdr1H4b8kAL';

// A tiny static asset to check if client network can reach Pandora api servers
export const NETWORK_PULSE_ENDPOINT = '/ping.txt';
// STEAM-4830 Alternative method for checking connectivity
export const NETWORK_RADIO_HEALTH_ENDPOINT = '/radio-health';
// If pinging takes too long, we deem the network connection inadequate
export const NETWORK_PULSE_TIMEOUT_MS = 4000;
export const NETWORK_POLL_INTERVAL_DEFAULT_MS = 20000;
// Poll a little more frequently when the app is offline
export const NETWORK_POLL_INTERVAL_OFFLINE_MS = 7000;
export const STITCHER_SUNSET = 'stitcher-sunset';

// Points in time when we want to know current datetime to calculate metrics
export const TimingMoments = {
  // Value set in index.vm
  TIMER_INIT: 'TIMER_INIT',
  START_APP: 'START_APP',
  STORE_INITIALIZED: 'STORE_INITIALIZED',
  APP_INITIALIZED: 'APP_INITIALIZED',
  FIRST_PAINT: 'FIRST_PAINT',
  APP_LAUNCHED_TTM: 'APP_LAUNCHED_TTM',
  READY_TO_PLAY: 'READY_TO_PLAY',
} as const;

export const TimingMetrics = {
  TIME_TO_START_APP: 'time_to_start_app',
  TIME_TO_INITIALIZE_STORE: 'time_to_initialize_store',
  TIME_TO_INITIALIZE_APP: 'time_to_initialize_app',
  TIME_TO_FIRST_PAINT: 'time_to_first_paint',
  TIME_TO_READY_TO_PLAY: 'time_to_ready_to_play',
  API_LOAD_TIME: 'api_load_time',
} as const;

// Used to set polling frequency and correct performance measurements
export const SEARCH_POLLING_FREQ_MSEC = 300;

/* Search Marks/Measures */

export const SearchTriggerMarks = {
  SEARCH_STATS_FILTER_CHANGE: 'filter_change',
  SEARCH_STATS_INPUT_CHANGE: 'input_change',
  SEARCH_STATS_ROUTE_CHANGE: 'route_change',
  SEARCH_STATS_SCROLL_QUERY: 'scroll_query',
} as const;

export const SearchCheckpointMarks = {
  SEARCH_STATS_RESULTS_RENDER: 'results_render',
  SEARCH_STATS_SOD_REQUEST: 'sod_request',
  SEARCH_STATS_SOD_RESPONSE: 'sod_response',
} as const;

export const SearchMeasures = {
  SEARCH_STATS_SOD_CALL_DURATION: 'sod_call_duration',
  SEARCH_STATS_TTS_DURATION: 'tts_duration',
} as const;

/* Discovery Marks/Measures */
export const DiscoveryMarks = {
  RECENTLY_PLAYED_GRAPHQL_START: 'recently_played_graphql_start',
} as const;

export const DiscoveryMeasures = {
  RECENTLY_PLAYED_GRAPHQL_DURATION: 'recently_played_graphql_duration',
} as const;

/* Playback Marks/Measures */

export const PlaybackMeasures = {
  AUDIO_PLAYER_TIME_TO_AUDIO_DURATION: 'AUDIO_PLAYER_TIME_TO_AUDIO_DURATION',
  BUFFERING_TIME: 'BUFFERING_TIME',
} as const;

export const PlaybackMarks = {
  AUDIO_PLAY: 'AUDIO_PLAY',
  AUDIO_PLAYING: 'AUDIO_PLAYING',
  BUFFERING_START: 'BUFFERING_START',
  BUFFERING_STOP: 'BUFFERING_STOP',
} as const;

// Delay to wait after app initializes to log events around it
export const POST_APP_INIT_LOGGING_DELAY_MS = 5000;

export const RouteActions = {
  PUSH: 'PUSH',
  POP: 'POP',
  REPLACE: 'REPLACE',
} as const;

export const StartupQueryParameterKeys = {
  OFFER_CODE: 'code',
  START_STATION_BY_CODE: 'sc',
  START_STATION_BY_MUSIC_ID: 'ext_lsfmi',
  ENABLE_LISTENER_SUPPORT_MENU: 'support',
  TIMEOUT: 'timeout',
  INACTIVE_TIMEOUT: 'inactive_timeout',
  ABANDONED_TIMEOUT: 'abandoned_timeout',
  RESET_PASSWORD_TOKEN: 'pt',
  ACCESS_TOKEN: 'accessToken',
  GREENFIELD_UPGRADE_FLAG: 'gfupgrade',
  TOKEN: 'token',
  LOGGING_LEVEL: 'logLevel',
  LOG_ACTIONS_TO_CONSOLE: 'logActionsToConsole',
  ENABLE_REDUX_DEVTOOL: 'enableReduxDevtool',
  ENABLE_ACTION_GROUP_LOGGER: 'enableActionGroupLogger',
  USERNAME: 'un',
  SKIP_STATS: 'skipStats',
  ENABLE_DEBUG_MENU: 'enableDebugMenu',
  FAMILY_PLAN_INVITE_TOKEN: 'fpi',
  STORE: 'store',
  FEATURE_CODE: 'featureCode',
  ENTITLEMENT_ID: 'entitlementId',
  REGISTER: 'reg',
  E_REQUEST_ID: 'eRequestId',
  COMMUNITY_SSO: 'communitysso',
  AMP_SSO: 'ampsso',
  REFERER: 'referer',
  MODE_ID: 'modeId',
} as const;

export const UrlSegments = Object.freeze({
  PLAY: 'play',
} as const);

export const DAILY_SKIP_TIMEOUT = 24 * 60 * 60 * 1000;
export const HOURLY_SKIP_TIMEOUT = 60 * 60 * 1000;
export const ANON_TIMEOUT = 14 * 60 * 1000;
export const NO_SKIP_LIMIT_SENTINEL = -1;
// for now this is coming from this static constant. In the future this shall come from Chronos v2
export const ANON_TRIAL_TIMEOUT_MESSAGE =
  'https://pandora.com/static/creatives/trial/Pandora_FTUXTimeout_IACS-1261_(JRuhff).mp3';

export const ApiMeasurements = {
  FETCH_STATIONS_CALL_DURATION: 'get_stations_duration',
  COLLECTIONS_GET_ITEMS_CALL_DURATION: 'get_items_duration',
  COLLECTIONS_GET_SORTED_PLAYLISTS_CALL_DURATION: 'get_playlists_duration',
} as const;

export const ApiCheckpointMarks = {
  FETCH_STATIONS_REQUEST: 'get_stations_request',
  FETCH_STATIONS_RESPONSE: 'get_stations_response',
  COLLECTIONS_GET_ITEMS_REQUEST: 'get_items_request',
  COLLECTIONS_GET_ITEMS_RESPONSE: 'get_items_response',
  COLLECTIONS_GET_SORTED_PLAYLISTS_REQUEST: 'get_playlists_request',
  COLLECTIONS_GET_SORTED_PLAYLISTS_RESPONSE: 'get_playlists_response',
} as const;
